import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';

import type { FeatureKey } from '@platform/contracts';
import { Alert, Card, PageHeader, Spinner, useToast } from '@platform/ui';

import { PlansMatrix } from '../../features/plans/plans-matrix';
import { plansKey } from '../../features/tenants/use-tenant';
import { FEATURE_LABELS, type CreatePlanRequest } from '../../lib/api/platform';
import { usePlatformClient } from '../../lib/api/platform-client-context';

export const Route = createFileRoute('/plans/')({
  component: PlansPage,
});

/** `/plans` — plans and the feature matrix (MASTER_PLAN M12.2, Docs/API.md §2.13). */
function PlansPage() {
  const client = usePlatformClient();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [createError, setCreateError] = useState<string | undefined>(undefined);

  const plans = useQuery({ queryKey: plansKey(), queryFn: () => client.listPlans() });

  const refresh = async (): Promise<void> => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: plansKey() }),
      // A plan's features decide what every tenant on it can do.
      queryClient.invalidateQueries({ queryKey: ['platform-tenant'] }),
    ]);
  };

  const toggle = useMutation({
    mutationFn: (input: { planId: string; featureKey: FeatureKey; enabled: boolean }) =>
      client.setPlanFeature(input.planId, input.featureKey, input.enabled),
    /*
     * M13.7 (WCAG 4.1.3 Status Messages). The matrix saves on change and, until now, said
     * so only when it FAILED: a successful tick moved a checkbox and nothing else, which
     * is no confirmation at all for a screen reader and a thin one for anybody, given the
     * tick decides what every tenant on that plan can do. The failure path already had a
     * toast; this is its other half.
     */
    onSuccess: async (_result, input) => {
      await refresh();
      toast({
        tone: 'success',
        message: `${FEATURE_LABELS[input.featureKey]} is now ${input.enabled ? 'on' : 'off'} for this plan.`,
      });
    },
    onError: (cause: Error) => {
      toast({ tone: 'error', message: cause.message });
    },
  });

  const create = useMutation({
    mutationFn: (input: CreatePlanRequest) => client.createPlan(input),
    onSuccess: async (plan) => {
      setCreateError(undefined);
      toast({ tone: 'success', message: `${plan.name} created with nothing included yet.` });
      await refresh();
    },
    onError: (cause: Error) => {
      setCreateError(cause.message);
    },
  });

  if (plans.isPending) {
    return (
      <>
        <PageHeader title="Plans" />
        <Card className="p-6">
          <Spinner label="Loading plans" />
        </Card>
      </>
    );
  }

  if (plans.isError) {
    return (
      <>
        <PageHeader title="Plans" />
        <Alert tone="error">The plans could not be loaded. Try again in a moment.</Alert>
      </>
    );
  }

  return (
    <PlansMatrix
      plans={plans.data}
      createError={createError}
      onToggle={(planId, featureKey, enabled) => {
        toggle.mutate({ planId, featureKey, enabled });
      }}
      onCreate={(input) => {
        create.mutate(input);
      }}
    />
  );
}
